import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetAppealQuery,
  useTraderApproveAppealMutation,
  useTraderRejectAppealMutation,
} from "../store/trader-api";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--text-secondary);
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: var(--text-primary);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const Card = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
`;

const Value = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-bg)";
      case "IN_PROGRESS":
        return "var(--alert-info-bg)";
      case "APPROVED":
        return "var(--status-success-bg)";
      case "REJECTED":
        return "var(--status-error-bg)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-bg)";
      case "MERCHANT_RESP_PENDING":
        return "var(--alert-warning-bg)";
      case "TRADER_REVIEW":
        return "var(--status-pending-bg)";
      case "SECONDARY_DISPUTE":
        return "var(--alert-error-bg)";
      default:
        return "var(--bg-secondary)";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-text)";
      case "IN_PROGRESS":
        return "var(--alert-info-text)";
      case "APPROVED":
        return "var(--status-success-text)";
      case "REJECTED":
        return "var(--status-error-text)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-text)";
      case "MERCHANT_RESP_PENDING":
        return "var(--alert-warning-text)";
      case "TRADER_REVIEW":
        return "var(--status-pending-text)";
      case "SECONDARY_DISPUTE":
        return "var(--alert-error-text)";
      default:
        return "var(--text-primary)";
    }
  }};
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FileItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-primary);
    background: var(--accent-primary-light);
  }
`;

const FileIcon = styled.div`
  width: 2rem;
  height: 2rem;
  background: var(--bg-secondary);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
`;

const FileName = styled.span`
  font-size: 0.75rem;
  text-align: center;
  word-break: break-all;
`;

const CommentBox = styled.div`
  background: var(--bg-primary);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  white-space: pre-wrap;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--accent-error);
`;

// Timeline styles (metro-style)
const TimelineContainer = styled.div`
  position: relative;
  padding-left: 2rem;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.5rem;
  
  &:last-child {
    padding-bottom: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: -1.5rem;
    top: 0.5rem;
    width: 12px;
    height: 12px;
    background: var(--accent-primary);
    border-radius: 50%;
    border: 2px solid var(--bg-card);
    box-shadow: 0 0 0 3px var(--accent-primary-border);
  }

  &::after {
    content: '';
    position: absolute;
    left: calc(-1.5rem + 5px);
    top: 1.5rem;
    width: 2px;
    height: calc(100% - 1rem);
    background: var(--border-color);
  }
  
  &:last-child::after {
    display: none;
  }
`;

const TimelineContent = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const TimelineField = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const TimelineDate = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const TimelineChange = styled.div`
  font-size: 0.8125rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimelineOldValue = styled.span`
  color: var(--accent-error);
  text-decoration: line-through;
`;

const TimelineNewValue = styled.span`
  color: var(--accent-success);
  font-weight: 500;
`;

const TimelineReason = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
  font-style: italic;
`;

// Action buttons
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button<{ variant: "approve" | "reject" }>`
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  background: ${(props) =>
    props.variant === "approve" ? "var(--accent-success)" : "var(--accent-error)"};
  color: #ffffff;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-lg);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ModalDescription = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const FileUploadArea = styled.div<{ hasFiles: boolean }>`
  border: 2px dashed ${(props) => (props.hasFiles ? "var(--accent-success)" : "var(--border-input)")};
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.hasFiles ? "var(--alert-success-bg)" : "transparent")};

  &:hover {
    border-color: var(--accent-primary);
    background: var(--bg-secondary);
  }
`;

const FileList = styled.div`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const UploadedFileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: var(--bg-secondary);
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--accent-error);
  padding: 0;
  font-size: 0.875rem;
  line-height: 1;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button<{ variant: "primary" | "secondary" }>`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background: ${(props) =>
    props.variant === "primary" ? "var(--accent-error)" : "transparent"};
  color: ${(props) => (props.variant === "primary" ? "#ffffff" : "var(--text-primary)")};
  border: 1px solid ${(props) =>
    props.variant === "primary" ? "var(--accent-error)" : "var(--border-input)"};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--accent-error);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ActionCard = styled(Card)`
  background: var(--alert-warning-bg);
  border: 1px solid var(--alert-warning-border);
`;

function AppealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: appeal, isLoading, error } = useGetAppealQuery(id!);

  // Action state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [validationErrors, setValidationErrors] = useState<{ comment?: string; files?: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API hooks
  const [traderApprove, { isLoading: isApproving }] = useTraderApproveAppealMutation();
  const [traderReject, { isLoading: isRejecting }] = useTraderRejectAppealMutation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    if (!status) return "—";
    const cleanStatus = status.replace(/"/g, '').toUpperCase();
    switch (cleanStatus) {
      case "PENDING":
        return "На рассмотрении";
      case "IN_PROGRESS":
        return "В работе";
      case "APPROVED":
        return "Одобрена";
      case "REJECTED":
        return "Отклонена";
      case "SUPPORT_REVIEW":
        return "На рассмотрении саппорта";
      case "TRADER_REVIEW":
        return "На повторном рассмотрении";
      case "MERCHANT_RESP_PENDING":
        return "На рассмотрении мерчанта";
      case "SECONDARY_DISPUTE":
        return "Вторичная апелляция";
      default:
        return status;
    }
  };

  const formatLogValue = (field: string, value: string | null): string => {
    if (!value) return "";
    const cleanValue = value.replace(/"/g, '');
    if (field === 'status') {
      return getStatusText(cleanValue);
    }
    return cleanValue;
  };

  const parseFileArray = (fileData: any) => {
    if (!fileData) return [];
    if (typeof fileData === "string") {
      try {
        return JSON.parse(fileData);
      } catch {
        return [];
      }
    }
    return Array.isArray(fileData) ? fileData : [];
  };

  const handleApprove = async () => {
    if (window.confirm("Вы уверены, что хотите одобрить апелляцию? Это означает, что платеж был получен.")) {
      try {
        await traderApprove(id!).unwrap();
      } catch (err) {
        console.error("Ошибка одобрения апелляции:", err);
        alert("Не удалось одобрить апелляцию");
      }
    }
  };

  const handleOpenRejectModal = () => {
    setRejectComment("");
    setFiles([]);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRejectComment("");
    setFiles([]);
    setValidationErrors({});
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
      setValidationErrors((prev) => ({ ...prev, files: undefined }));
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReject = async () => {
    const newErrors: { comment?: string; files?: string } = {};

    if (!rejectComment.trim()) {
      newErrors.comment = "Необходимо указать причину отклонения";
    }

    if (files.length === 0) {
      newErrors.files = "Необходимо приложить доказательство";
    }

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("comment", rejectComment);
      files.forEach((file) => {
        formData.append("files", file);
      });

      await traderReject({ appealId: id!, formData }).unwrap();
      handleCloseModal();
    } catch (err) {
      console.error("Ошибка отклонения апелляции:", err);
      alert("Не удалось отклонить апелляцию");
    }
  };

  if (isLoading) {
    return <LoadingContainer>Загрузка апелляции...</LoadingContainer>;
  }

  if (error || !appeal) {
    return <ErrorContainer>Ошибка загрузки апелляции</ErrorContainer>;
  }

  const images = parseFileArray(appeal.images);
  const videos = parseFileArray(appeal.videos);
  const documents = parseFileArray(appeal.documents);
  const allFiles = [...images, ...videos, ...documents];
  const isPending = appeal.status?.toUpperCase() === "PENDING" || appeal.status?.toUpperCase() === "TRADER_REVIEW";

  return (
    <>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate("/appeals")}>
              ← Назад к апелляциям
            </BackButton>
            <Title>Апелляция #{appeal.id.substring(0, 8)}</Title>
          </HeaderLeft>
          
          {isPending && (
            <ActionButtonsContainer>
              <ActionButton
                variant="approve"
                onClick={handleApprove}
                disabled={isApproving}
              >
                ✓ Одобрить
              </ActionButton>
              <ActionButton
                variant="reject"
                onClick={handleOpenRejectModal}
                disabled={isRejecting}
              >
                ✗ Отклонить
              </ActionButton>
            </ActionButtonsContainer>
          )}
        </Header>

        {/* Action Card for pending appeals */}
        {isPending && (
          <ActionCard>
            <SectionTitle>⚠️ Требуется ваше решение</SectionTitle>
            <p style={{ margin: 0, color: "var(--alert-warning-text)", fontSize: "0.875rem" }}>
              Клиент утверждает, что платеж был произведен. Проверьте поступление средств и примите решение:
            </p>
            <ul style={{ margin: "0.75rem 0 0 0", paddingLeft: "1.25rem", color: "var(--alert-warning-text)", fontSize: "0.875rem" }}>
              <li><strong>Одобрить</strong> — если платеж действительно поступил</li>
              <li><strong>Отклонить</strong> — если платеж не поступил (потребуется приложить доказательства)</li>
            </ul>
          </ActionCard>
        )}

        <Card>
          <SectionTitle>Основная информация</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <Label>ID Апелляции</Label>
              <Value>{appeal.id}</Value>
            </InfoItem>
            <InfoItem>
              <Label>ID Ордера</Label>
              <Value>{appeal.order?.invId || appeal.orderId}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Статус</Label>
              <StatusBadge status={appeal.status}>
                {getStatusText(appeal.status)}
              </StatusBadge>
            </InfoItem>
            <InfoItem>
              <Label>Создана</Label>
              <Value>{formatDate(appeal.createdAt)}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Обновлена</Label>
              <Value>
                {appeal.updatedAt ? formatDate(appeal.updatedAt) : "—"}
              </Value>
            </InfoItem>
            <InfoItem>
              <Label>Причина</Label>
              <Value>{appeal.reason}</Value>
            </InfoItem>
          </InfoGrid>
        </Card>

        {appeal.order && (
          <Card>
            <SectionTitle>Детали ордера</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <Label>Сумма</Label>
                <Value>
                  {appeal.order.amount} {appeal.order.currency}
                </Value>
              </InfoItem>
              <InfoItem>
                <Label>Статус ордера</Label>
                <Value>{appeal.order.status}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Банк</Label>
                <Value>{appeal.order.bank}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Реквизит</Label>
                <Value>
                  {appeal.order.requisite ? (
                    <>
                      {appeal.order.requisite.cardNumber || appeal.order.requisite.phoneNumber || appeal.order.requisite.sbpPhoneNumber || appeal.order.requisite.accountNumber || "—"}
                      {appeal.order.requisite.bank?.name && (
                        <span style={{ color: "var(--text-secondary)", marginLeft: 4, fontSize: "0.75rem" }}>
                          ({appeal.order.requisite.bank.name})
                        </span>
                      )}
                    </>
                  ) : "—"}
                </Value>
              </InfoItem>
            </InfoGrid>
          </Card>
        )}

        {appeal.comment && (
          <Card>
            <SectionTitle>Комментарий</SectionTitle>
            <CommentBox>{appeal.comment}</CommentBox>
          </Card>
        )}

        {allFiles.length > 0 && (
          <Card>
            <SectionTitle>Прикрепленные файлы клиента ({allFiles.length})</SectionTitle>
            <FileGrid>
              {allFiles.map((file, index) => {
                const fileUrl = file.url || file.path;
                // Strip /api from API URL since static files are served from /uploads not /api/uploads
                const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api\/trader$/, '').replace(/\/api$/, '');
                const fullUrl = fileUrl?.startsWith('http') ? fileUrl : `${baseUrl}${fileUrl}`;
                const isImage = file.mimetype?.startsWith('image/') || file.originalname?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                
                return (
                  <FileItem
                    key={index}
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileIcon>
                      {isImage ? "🖼️" : file.mimetype?.startsWith('video/') ? "🎥" : "📄"}
                    </FileIcon>
                    <FileName>
                      {file.originalname || file.filename || "Файл"}
                    </FileName>
                  </FileItem>
                );
              })}
            </FileGrid>
          </Card>
        )}

        {/* История доказательств трейдера — таблица из changeLogs */}
        {(() => {
          // Собираем ВСЕ записи доказательств из changeLogs + текущий traderEvidence
          const evidenceLogs = (appeal.changeLogs || [])
            .filter((log: any) => log.field === 'traderEvidence')
            .reverse(); // хронологический порядок (changeLogs приходят desc)
          
          // Парсим текущий traderEvidence
          let currentFiles: any[] = [];
          let currentComment = "";
          if (appeal.traderEvidence) {
            try {
              const evidence = typeof appeal.traderEvidence === "string" 
                ? JSON.parse(appeal.traderEvidence) 
                : appeal.traderEvidence;
              if (evidence.files && Array.isArray(evidence.files)) currentFiles = evidence.files;
              if (evidence.comment) currentComment = evidence.comment;
            } catch (e) {}
          }
          
          const hasHistory = evidenceLogs.length > 0;
          const hasCurrent = currentFiles.length > 0 || currentComment || appeal.traderComment;
          
          if (!hasHistory && !hasCurrent) return null;
          
          const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api\/trader$/, '').replace(/\/api$/, '');
          
          const renderFiles = (files: any[]) => (
            <FileGrid>
              {files.map((file: any, index: number) => {
                const fileUrl = file.url || file.path || '';
                const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${base}${fileUrl.startsWith('/') ? fileUrl : '/' + fileUrl}`;
                return (
                  <FileItem key={index} href={fullUrl} target="_blank" rel="noopener noreferrer">
                    <FileIcon>
                      {file.originalName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? "🖼️" :
                       file.originalName?.match(/\.(mp4|avi|mov|webm)$/i) ? "🎥" : "📄"}
                    </FileIcon>
                    <FileName>{file.originalName || "Файл"}</FileName>
                  </FileItem>
                );
              })}
            </FileGrid>
          );
          
          return (
            <Card>
              <SectionTitle>Мои доказательства</SectionTitle>
              {hasHistory ? (
                // Показываем историю из changeLogs
                <TimelineContainer>
                  {evidenceLogs.map((log: any, idx: number) => {
                    let files: any[] = [];
                    let comment = "";
                    let submittedAt = "";
                    try {
                      let ev = typeof log.newValue === 'string' ? JSON.parse(log.newValue) : log.newValue;
                      // Обработка двойной сериализации (аудит-сервис делает JSON.stringify на уже stringify-ed строке)
                      if (typeof ev === 'string') ev = JSON.parse(ev);
                      if (ev?.files && Array.isArray(ev.files)) files = ev.files;
                      if (ev?.comment) comment = ev.comment;
                      if (ev?.submittedAt) submittedAt = new Date(ev.submittedAt).toLocaleString('ru-RU');
                    } catch (e) {}
                    
                    return (
                      <TimelineItem key={log.id || idx}>
                        <TimelineContent>
                          <TimelineHeader>
                            <TimelineField>Доказательство #{idx + 1}</TimelineField>
                            <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                          </TimelineHeader>
                          {files.length > 0 && renderFiles(files)}
                          {comment && (
                            <CommentBox style={{ marginTop: '0.5rem' }}>{comment}</CommentBox>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </TimelineContainer>
              ) : (
                // Fallback: показать текущий traderEvidence без changeLogs
                <>
                  {currentFiles.length > 0 && renderFiles(currentFiles)}
                  {(currentComment || appeal.traderComment) && (
                    <CommentBox style={{ marginTop: currentFiles.length > 0 ? "1rem" : 0 }}>
                      {currentComment || appeal.traderComment}
                    </CommentBox>
                  )}
                </>
              )}
            </Card>
          );
        })()}

        {/* Комментарий админа для трейдера (из changeLogs supportComment) */}
        {(() => {
          const adminComments = (appeal.changeLogs || [])
            .filter((log: any) => log.field === 'supportComment')
            .reverse();
          if (adminComments.length === 0) return null;
          return (
            <Card style={{ border: '1px solid var(--alert-info-border)' }}>
              <SectionTitle>Комментарии саппорта</SectionTitle>
              <TimelineContainer>
                {adminComments.map((log: any, idx: number) => (
                  <TimelineItem key={log.id || idx}>
                    <TimelineContent>
                      <TimelineHeader>
                        <TimelineField>Саппорт</TimelineField>
                        <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                      </TimelineHeader>
                      <CommentBox style={{ marginTop: '0.25rem' }}>{log.newValue}</CommentBox>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </TimelineContainer>
            </Card>
          );
        })()}

        {/* История изменений — фильтруем merchantEvidence и supportComment для мерчанта */}
        <Card>
          <SectionTitle>История изменений</SectionTitle>
          {appeal.changeLogs && appeal.changeLogs.length > 0 ? (
            <TimelineContainer>
              {appeal.changeLogs
                .filter((log: any) => {
                  // Скрываем merchantEvidence (файлы мерчанта)
                  if (log.field === 'merchantEvidence') return false;
                  // Скрываем supportComment (показаны отдельно выше)
                  if (log.field === 'supportComment') return false;
                  // Скрываем traderEvidence (показаны в секции "Мои доказательства")
                  if (log.field === 'traderEvidence') return false;
                  return true;
                })
                .map((log: any) => {
                const fieldLabel = log.field === 'status' ? 'Изменение статуса' : 
                         log.field === 'comment' ? 'Комментарий' :
                         log.field === 'deadline' ? 'Дедлайн' :
                         log.field;

                return (
                <TimelineItem key={log.id}>
                  <TimelineContent>
                    <TimelineHeader>
                      <TimelineField>{fieldLabel}</TimelineField>
                      <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                    </TimelineHeader>
                    <TimelineChange>
                      {log.oldValue && (
                        <TimelineOldValue>{formatLogValue(log.field, log.oldValue)}</TimelineOldValue>
                      )}
                      {log.oldValue && log.newValue && <span>→</span>}
                      {log.newValue && (
                        <TimelineNewValue>{formatLogValue(log.field, log.newValue)}</TimelineNewValue>
                      )}
                    </TimelineChange>
                    {log.reason && (
                      <TimelineReason>{log.reason}</TimelineReason>
                    )}
                  </TimelineContent>
                </TimelineItem>
                );
              })}
              
              {/* Создание апелляции — в конце */}
              <TimelineItem>
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineField>Апелляция создана</TimelineField>
                    <TimelineDate>{formatDate(appeal.createdAt)}</TimelineDate>
                  </TimelineHeader>
                  <TimelineChange>
                    <TimelineNewValue>Статус: На рассмотрении</TimelineNewValue>
                  </TimelineChange>
                  {appeal.reason && (
                    <TimelineReason>Причина: {appeal.reason}</TimelineReason>
                  )}
                </TimelineContent>
              </TimelineItem>
            </TimelineContainer>
          ) : (
            <TimelineContainer>
              <TimelineItem>
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineField>Апелляция создана</TimelineField>
                    <TimelineDate>{formatDate(appeal.createdAt)}</TimelineDate>
                  </TimelineHeader>
                  <TimelineChange>
                    <TimelineNewValue>Статус: {getStatusText(appeal.status)}</TimelineNewValue>
                  </TimelineChange>
                  {appeal.reason && (
                    <TimelineReason>Причина: {appeal.reason}</TimelineReason>
                  )}
                </TimelineContent>
              </TimelineItem>
            </TimelineContainer>
          )}
        </Card>
      </Container>

      {/* Модальное окно для отклонения */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Отклонение апелляции</ModalTitle>
            <ModalDescription>
              Для отклонения апелляции необходимо приложить доказательство и указать причину. 
              После отклонения апелляция будет передана на рассмотрение саппорта.
            </ModalDescription>

            <FormGroup>
              <FormLabel>Доказательство (обязательно)</FormLabel>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept="image/*,video/*,.pdf"
                style={{ display: "none" }}
              />
              <FileUploadArea
                hasFiles={files.length > 0}
                onClick={() => fileInputRef.current?.click()}
              >
                {files.length === 0 ? (
                  <>
                    <div>📎 Нажмите для загрузки файлов</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                      Изображения, видео или PDF
                    </div>
                  </>
                ) : (
                  <div style={{ color: "var(--accent-success)" }}>
                    ✓ Загружено {files.length} файл(ов)
                  </div>
                )}
              </FileUploadArea>
              {files.length > 0 && (
                <FileList>
                  {files.map((file, index) => (
                    <UploadedFileItem key={index}>
                      {file.name}
                      <RemoveFileButton onClick={() => removeFile(index)}>
                        ×
                      </RemoveFileButton>
                    </UploadedFileItem>
                  ))}
                </FileList>
              )}
              {validationErrors.files && <ErrorMessage>{validationErrors.files}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Причина отклонения (обязательно)</FormLabel>
              <TextArea
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  setValidationErrors((prev) => ({ ...prev, comment: undefined }));
                }}
                placeholder="Опишите причину отклонения апелляции..."
              />
              {validationErrors.comment && <ErrorMessage>{validationErrors.comment}</ErrorMessage>}
            </FormGroup>

            <ModalButtons>
              <ModalButton variant="secondary" onClick={handleCloseModal}>
                Отмена
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={handleReject}
                disabled={isRejecting}
              >
                {isRejecting ? "Отправка..." : "Отклонить апелляцию"}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default AppealDetail;
